import React, { FC, MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components';

import Eyebrow from '@ui/atoms/Eyebrow';

import { exists } from '@utils/general';

import { FontSizeTypes, RadiusTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

type IBaseButtonProps = {
	label: string | ReactNode;
	ariaLabel?: string;
	className?: string;
	disabled?: boolean;
	href?: string | URL;
	labelSize?: FontSizeTypes;
	newTab?: boolean;
	type?: 'button' | 'submit' | 'reset';
	onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
	dataCy?: string;
	radius?: RadiusTypes;
};

export type IButtonProps = {
	label: string | ReactNode;
	ariaLabel?: string;
	className?: string;
	variant?: 'default' | 'secondary';
	disabled?: boolean;
	href?: string;
	newTab?: boolean;
	type?: 'button' | 'submit' | 'reset' | undefined;
	labelSize?: FontSizeTypes;
	onClick?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
	dataCy?: string;
	radius?: RadiusTypes;
};

const ButtonLabel = styled(Eyebrow)`
	font-size: 1.8rem;
	padding: ${({ theme: components }) => components.button.padding};

	@media ${theme.responsive.breakpoints.tablet} {
		font-size: 2rem;
	}
`;

const BaseButton: FC<IBaseButtonProps> = ({
	ariaLabel,
	className,
	href,
	label,
	newTab,
	type,
	onClick = () => {},
	radius,
	disabled = false,
	dataCy,
}) => {
	const Component = href ? 'a' : 'button';

	return (
		<Component
			aria-label={ariaLabel}
			className={className}
			disabled={disabled}
			href={href}
			data-cy={dataCy}
			onClick={onClick}
			radius={radius}
			target={newTab ? '_blank' : undefined}
			type={type ?? 'button'}>
			{typeof label === 'string' ? <ButtonLabel>{label}</ButtonLabel> : label}
		</Component>
	);
};

const DefaultButton = styled(BaseButton)`
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	background: ${({ theme: components }) => {
		const {
			button: { primary },
		} = components;

		return primary.bgTint(primary.bgColor);
	}};
	border: ${({ theme: components }) =>
		components.button.primary.border ??
		`1px solid ${components.button.primary.bgTint(components.button.primary.bgColor)}`};
	border-radius: ${({ theme: components, radius }) =>
		exists(radius) ? theme.radius[radius] : components.button.borderRadius};
	padding: 0;
	transition: all ease-in 0.2s, outline-width linear 0s;
	text-decoration: none;
	width: ${({ theme: components }) =>
		components.button.primary?.width ? components.button.primary?.width : components.button.width};

	${ButtonLabel}, ${Eyebrow} {
		color: ${({ theme: components }) => {
			const {
				button: { primary },
			} = components;

			return primary.labelColor;
		}};

		font-size: ${({ theme: components, labelSize }) => {
			const {
				button: { primary },
			} = components;
			return labelSize ? theme.fontSizes.body[labelSize] : primary.labelSize;
		}};

		padding: ${({ theme: components }) => {
			const {
				button: { secondary },
			} = components;

			return secondary.padding;
		}};
	}

	&:disabled {
		border: ${({ theme: components }) => components.button.primary.disabledBorder ?? 0};
		background: ${({ theme: components }) => {
			const {
				button: { primary },
			} = components;

			return primary.disabledTint(primary.disabledColor);
		}};

		${ButtonLabel}, ${Eyebrow} {
			color: ${({ theme: components }) => {
				const {
					button: { primary },
				} = components;

				return primary.disabledLabelTint(primary.disabledLabelColor);
			}};
		}
	}

	&:disabled:hover {
		background: ${({ theme: components }) => {
			const {
				button: { primary },
			} = components;

			return primary.disabledTint(primary.disabledColor);
		}};

		${ButtonLabel}, ${Eyebrow} {
			color: ${({ theme: components }) => {
				const {
					button: { primary },
				} = components;

				return primary.disabledLabelTint(primary.disabledLabelColor);
			}};
		}
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:hover,
	&:focus-visible {
		border: ${({ theme: components }) =>
			components.button.primary.focusBorder ??
			`1px solid ${components.button.primary.focusTint(components.button.primary.focusColor)}`};
		background: ${({ theme: components }) => {
			const {
				button: { primary },
			} = components;

			return primary.focusTint(primary.focusColor);
		}};
		transition: all ease-out 0.2s, outline-width linear 0s;

		${ButtonLabel}, ${Eyebrow} {
			color: ${({ theme: components }) => {
				const {
					button: { primary },
				} = components;

				return primary.focusLabelTint(primary.focusLabelColor);
			}};
		}
	}
`;

const SecondaryButton = styled(DefaultButton)`
	border: ${({ theme: components }) =>
		components.button.secondary.border ??
		`1px solid ${components.button.secondary.bgTint(components.button.secondary.bgColor)}`};
	border-radius: ${({ theme: components }) =>
		components.button.secondary.borderRadius ?? components.button.secondary.borderRadius};
	background: ${({ theme: components }) => {
		const {
			button: { secondary },
		} = components;

		return secondary.bgTint(secondary.bgColor);
	}};
	transition: all ease-in 0.2s, outline-width linear 0s;
	width: ${({ theme: components }) =>
		components.button.secondary?.width
			? components.button.secondary?.width
			: components.button.width};

	${ButtonLabel}, ${Eyebrow} {
		color: ${({ theme: components }) => {
			const {
				button: { secondary },
			} = components;

			return secondary.labelColor;
		}};

		font-size: ${({ theme: components, labelSize }) => {
			const {
				button: { secondary },
			} = components;

			return labelSize ? theme.fontSizes.body[labelSize] : secondary.labelSize;
		}};

		padding: ${({ theme: components }) => {
			const {
				button: { secondary },
			} = components;

			return secondary.padding;
		}};
	}

	&:disabled {
		border: ${({ theme: components }) => components.button.secondary.disabledBorder ?? 0};
		background: ${({ theme: components }) => {
			const {
				button: { secondary },
			} = components;

			return secondary.disabledTint(secondary.disabledColor);
		}};

		${ButtonLabel}, ${Eyebrow} {
			color: ${({ theme: components }) => {
				const {
					button: { secondary },
				} = components;

				return secondary.disabledLabelTint(secondary.disabledLabelColor);
			}};
		}
	}

	&:disabled:hover {
		border: ${({ theme: components }) => components.button.secondary.disabledBorder ?? 0};
		background: ${({ theme: components }) => {
			const {
				button: { secondary },
			} = components;

			return secondary.disabledTint(secondary.disabledColor);
		}};

		${ButtonLabel}, ${Eyebrow} {
			color: ${({ theme: components }) => {
				const {
					button: { secondary },
				} = components;

				return secondary.disabledLabelTint(secondary.disabledLabelColor);
			}};
		}
	}

	&:focus:not(:focus-visible) {
		outline: none;
	}

	&:hover,
	&:focus-visible {
		border: ${({ theme: components }) =>
			components.button.secondary.focusBorder ??
			`1px solid ${components.button.secondary.focusTint(components.button.secondary.focusColor)}`};
		background: ${({ theme: components }) => {
			const {
				button: { secondary },
			} = components;

			return secondary.focusTint(secondary.focusColor);
		}};
		transition: all ease-out 0.2s, outline-width linear 0s;

		${ButtonLabel}, ${Eyebrow} {
			color: ${({ theme: components }) => {
				const {
					button: { secondary },
				} = components;

				return secondary.focusLabelTint(secondary.focusLabelColor);
			}};
		}
	}
`;

const Button: FC<IButtonProps> = ({
	label,
	labelSize,
	variant,
	type,
	newTab,
	href,
	onClick,
	className,
	disabled,
	dataCy,
	radius,
}) => {
	if (variant === 'secondary') {
		return (
			<SecondaryButton
				href={href}
				type={type}
				newTab={newTab}
				onClick={onClick}
				label={label}
				labelSize={labelSize}
				className={className}
				disabled={disabled}
				dataCy={dataCy}
				radius={radius}
			/>
		);
	}

	return (
		<DefaultButton
			href={href}
			type={type}
			newTab={newTab}
			onClick={onClick}
			label={label}
			labelSize={labelSize}
			className={className}
			disabled={disabled}
			dataCy={dataCy}
			radius={radius}
		/>
	);
};

export default styled(Button)``;
